import { type JSX, useState } from "react";

interface JsonViewProps {
  value: unknown;
  /** Depth to expand automatically. Deeper nodes start collapsed. */
  openDepth?: number;
}

/**
 * A collapsible JSON viewer.
 *
 * The fallback renderer, and the one behind every "technical details" panel. Purpose-built
 * renderers for recall hits, citations, lineage and the rest arrive with the renderer pass; this
 * is what keeps an operation with no bespoke renderer readable rather than a wall of text.
 */
export function JsonView({ value, openDepth = 2 }: JsonViewProps): JSX.Element {
  return (
    <div className="json">
      <Node value={value} depth={0} openDepth={openDepth} label={null} />
    </div>
  );
}

interface NodeProps {
  value: unknown;
  depth: number;
  openDepth: number;
  label: string | null;
}

function Node({ value, depth, openDepth, label }: NodeProps): JSX.Element {
  const [open, setOpen] = useState(depth < openDepth);

  if (value === null || typeof value !== "object") {
    return (
      <div className="json__row" style={{ paddingLeft: `${depth}rem` }}>
        {label !== null && <span className="json__key">{label}: </span>}
        <Scalar value={value} />
      </div>
    );
  }

  const entries: Array<[string, unknown]> = Array.isArray(value)
    ? value.map((item, index) => [String(index), item])
    : Object.entries(value as Record<string, unknown>);
  const summary = Array.isArray(value) ? `[${entries.length}]` : `{${entries.length}}`;

  return (
    <div>
      <div className="json__row" style={{ paddingLeft: `${depth}rem` }}>
        <button type="button" className="json__toggle" onClick={() => setOpen(!open)}>
          {open ? "▾" : "▸"}
        </button>
        {label !== null && <span className="json__key">{label}: </span>}
        <span className="json__summary">{summary}</span>
      </div>
      {open &&
        entries.map(([key, item]) => (
          <Node key={key} value={item} depth={depth + 1} openDepth={openDepth} label={key} />
        ))}
    </div>
  );
}

function Scalar({ value }: { value: unknown }): JSX.Element {
  if (value === null) {
    return <span className="json__null">null</span>;
  }
  if (typeof value === "string") {
    return <span className="json__string">{value}</span>;
  }
  if (typeof value === "boolean") {
    return <span className="json__bool">{value ? "true" : "false"}</span>;
  }
  return <span className="json__number">{String(value)}</span>;
}
